import React, { useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Ruler, Activity, RotateCcw, Info, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { calculateCaliper } from '../services/api';
import { SaveCalculatorButton } from './SaveCalculatorButton';
import CaliperSitesIllustration from './CaliperSitesIllustration';

/**
 * Skinfold Body-Fat calculator using Jackson–Pollock 3-site / 7-site formulas
 * + Siri equation. Supports toggling between 3-site and 7-site protocols.
 *
 * Required sites per protocol:
 *   • JP-3 male   → chest, abdomen, thigh
 *   • JP-3 female → triceps, suprailiac, thigh
 *   • JP-7 both   → chest, midaxillary, triceps, subscapular,
 *                   abdomen, suprailiac, thigh
 */

const initialForm = {
  method:    'jp3',
  gender:    'male',
  age:       '',
  weight_kg: '',
  // Skinfold fields (all optional in state — validated per method at submit)
  chest_mm:       '',
  abdomen_mm:     '',
  thigh_mm:       '',
  triceps_mm:     '',
  suprailiac_mm:  '',
  midaxillary_mm: '',
  subscapular_mm: '',
};

// Which sites appear per method+gender (order controls form layout).
const SITES_JP3 = {
  male:   ['chest_mm', 'abdomen_mm', 'thigh_mm'],
  female: ['triceps_mm', 'suprailiac_mm', 'thigh_mm'],
};
const SITES_JP7 = [
  'chest_mm', 'midaxillary_mm', 'triceps_mm',
  'subscapular_mm', 'abdomen_mm', 'suprailiac_mm', 'thigh_mm',
];

const SITE_LABELS = {
  chest_mm:       { hu: 'Mellkas',       n: 1, hint: 'Ferde redő a hónalj és a mellbimbó között (férfiaknál).' },
  midaxillary_mm: { hu: 'Hónaljvonal',   n: 2, hint: 'Függőleges redő a hónaljvonalban, a xiphoid magasságában.' },
  triceps_mm:     { hu: 'Tricepsz',      n: 3, hint: 'Függőleges redő a tricepsz középvonalán (nőknél).' },
  subscapular_mm: { hu: 'Lapocka alatt', n: 4, hint: 'Ferde redő 1-2 cm-rel a lapocka alsó csúcsa alatt.' },
  abdomen_mm:     { hu: 'Has',           n: 5, hint: 'Függőleges redő a köldöktől 2 cm-re oldalt.' },
  suprailiac_mm:  { hu: 'Csípőlapát',    n: 6, hint: 'Ferde redő a csípőlapát felett, hónaljvonalban.' },
  thigh_mm:       { hu: 'Comb',          n: 7, hint: 'Függőleges redő a comb középvonalán, csípő és térd között félúton.' },
};

const CATEGORY_STYLES = {
  essential: { chip: 'bg-blue-100 text-blue-800 border-blue-200',  bar: 'bg-blue-500'  },
  athlete:   { chip: 'bg-green-100 text-green-800 border-green-200', bar: 'bg-green-500' },
  fitness:   { chip: 'bg-emerald-100 text-emerald-800 border-emerald-200', bar: 'bg-emerald-500' },
  average:   { chip: 'bg-amber-100 text-amber-800 border-amber-200', bar: 'bg-amber-500' },
  obese:     { chip: 'bg-red-100 text-red-800 border-red-200', bar: 'bg-red-500' },
};

const CaliperCalculator = ({ defaultGender } = {}) => {
  const [form, setForm]     = useState(() => ({ ...initialForm, gender: defaultGender || initialForm.gender }));
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const requiredSites = useMemo(() => {
    if (form.method === 'jp7') return SITES_JP7;
    return SITES_JP3[form.gender];
  }, [form.method, form.gender]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setForm(initialForm);
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (!form.age || Number(form.age) < 14 || Number(form.age) > 100) {
      toast.error('Kérlek add meg az életkorod (14–100 év).');
      return;
    }
    for (const site of requiredSites) {
      const v = Number(form[site]);
      if (!v || v <= 1 || v >= 80) {
        toast.error(`A(z) "${SITE_LABELS[site].hu}" mező kitöltése kötelező (1–80 mm).`);
        return;
      }
    }

    // Build payload — only send fields that are relevant for the chosen method
    const payload = {
      method: form.method,
      gender: form.gender,
      age:    parseInt(form.age, 10),
    };
    if (form.weight_kg) payload.weight_kg = parseFloat(form.weight_kg);
    for (const site of requiredSites) {
      payload[site] = parseFloat(form[site]);
    }

    setIsCalculating(true);
    try {
      const res = await calculateCaliper(payload);
      setResult(res);
      toast.success('Testzsírszázalék kiszámolva!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Hiba a számítás során — próbáld újra.');
    } finally {
      setIsCalculating(false);
    }
  };

  const categoryStyle = result ? CATEGORY_STYLES[result.category_key] : null;
  const barWidth = result ? Math.min(100, Math.max(2, (result.body_fat_pct / 40) * 100)) : 0;

  return (
    <section
      id="kalipper"
      data-testid="caliper-calculator-section"
      className="bg-gray-50 py-20"
    >
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Ruler size={16} />
            KALIPPER TESTZSÍRMÉRÉS
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            data-testid="caliper-heading"
          >
            Precíz testzsírszázalék bőrredő méréssel
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            A tudományos irodalom szerint a bőrredő mérés (kaliperelés) a legpontosabb <em>kézi</em> módszer
            a testzsír meghatározására. A Jackson-Pollock 3-pontos vagy 7-pontos protokollt támogatjuk.
          </p>
        </div>

        {/* Form + Results */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Method toggle */}
            <div>
              <Label className="text-gray-900 font-semibold mb-2 block">Mérési protokoll</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: 'jp3', l: 'Jackson-Pollock 3 pontos',  s: 'Gyors, jó közelítés' },
                  { v: 'jp7', l: 'Jackson-Pollock 7 pontos', s: 'Legpontosabb, ~1-2% szórás' },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => handleChange('method', opt.v)}
                    data-testid={`caliper-method-${opt.v}`}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      form.method === opt.v
                        ? 'border-yellow-600 bg-yellow-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-bold text-gray-900 text-sm break-words">{opt.l}</div>
                    <div className="text-xs text-gray-500 mt-1 break-words leading-snug">{opt.s}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Gender + Age + Weight */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-900 font-semibold mb-2 block">Nem</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: 'male',   l: 'Férfi' },
                    { v: 'female', l: 'Nő'    },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => handleChange('gender', opt.v)}
                      data-testid={`caliper-gender-${opt.v}`}
                      className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                        form.gender === opt.v
                          ? 'border-yellow-600 bg-yellow-50 text-yellow-800'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="caliper-age" className="text-gray-900 font-semibold mb-2 block">
                  Életkor (év)
                </Label>
                <Input
                  id="caliper-age"
                  data-testid="caliper-age"
                  type="number"
                  min="14"
                  max="100"
                  value={form.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  placeholder="30"
                  className="h-12"
                />
              </div>

              <div>
                <Label htmlFor="caliper-weight" className="text-gray-900 font-semibold mb-2 block">
                  Testsúly (kg) <span className="text-gray-400 text-xs">opcionális</span>
                </Label>
                <Input
                  id="caliper-weight"
                  data-testid="caliper-weight"
                  type="number"
                  min="20"
                  max="300"
                  step="0.1"
                  value={form.weight_kg}
                  onChange={(e) => handleChange('weight_kg', e.target.value)}
                  placeholder="80"
                  className="h-12"
                />
              </div>
            </div>

            {/* Skinfold fields */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-gray-900 font-semibold">
                  Bőrredő értékek (mm)
                </Label>
                <a
                  href="/tudastar/kalipper-utmutato"
                  className="text-xs text-yellow-700 hover:text-yellow-800 font-semibold inline-flex items-center gap-1"
                  data-testid="caliper-guide-link"
                >
                  <Info size={12} /> Hogyan mérj helyesen?
                </a>
              </div>

              {/* Anatomical illustration with numbered site markers */}
              <div
                className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg"
                data-testid="caliper-sites-illustration"
              >
                <div className="mx-auto md:mx-0 w-full max-w-[180px]">
                  <CaliperSitesIllustration />
                </div>
                <div className="text-xs text-gray-600">
                  <p className="font-semibold text-gray-900 mb-2">Mérési pontok referencia térkép</p>
                  <ol className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] leading-snug">
                    {Object.entries(SITE_LABELS)
                      .sort((a, b) => a[1].n - b[1].n)
                      .map(([, info]) => (
                        <li key={info.n} className="flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-yellow-600 text-white text-[9px] font-bold flex-shrink-0">
                            {info.n}
                          </span>
                          <span>{info.hu}</span>
                        </li>
                      ))}
                  </ol>
                  <p className="mt-2 text-[10px] text-gray-400">
                    A számozott pontok az input mezők előtt is megjelennek, hogy könnyen összepárosíthasd.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {requiredSites.map((site) => (
                  <div key={site}>
                    <Label
                      htmlFor={`caliper-${site}`}
                      className="text-gray-700 text-sm mb-1 flex items-center gap-1.5"
                    >
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-600 text-white text-[10px] font-bold">
                        {SITE_LABELS[site].n}
                      </span>
                      {SITE_LABELS[site].hu}
                    </Label>
                    <Input
                      id={`caliper-${site}`}
                      data-testid={`caliper-${site}`}
                      type="number"
                      min="1"
                      max="80"
                      step="0.5"
                      value={form[site]}
                      onChange={(e) => handleChange(site, e.target.value)}
                      placeholder="mm"
                      className="h-11"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 leading-relaxed">
                      {SITE_LABELS[site].hint}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                disabled={isCalculating}
                data-testid="caliper-calculate-btn"
                className="flex-1 h-12 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
              >
                {isCalculating ? 'Számolás...' : 'Testzsírszázalék kiszámítása'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                data-testid="caliper-reset-btn"
                className="h-12 border-gray-300"
              >
                <RotateCcw size={16} className="mr-2" />
                Új mérés
              </Button>
            </div>
          </form>

          {/* Results */}
          {result && (
            <div
              data-testid="caliper-result"
              className="mt-8 pt-8 border-t border-gray-200"
            >
              {/* Save-to-account CTA — visible only for logged-in athletes. */}
              <div className="flex justify-end mb-4">
                <SaveCalculatorButton
                  type="kaliper"
                  input={{
                    method: form.method, gender: form.gender, age: form.age,
                    weight_kg: form.weight_kg,
                    chest_mm: form.chest_mm, abdomen_mm: form.abdomen_mm,
                    thigh_mm: form.thigh_mm, triceps_mm: form.triceps_mm,
                    suprailiac_mm: form.suprailiac_mm,
                    midaxillary_mm: form.midaxillary_mm, subscapular_mm: form.subscapular_mm,
                  }}
                  output={{
                    body_fat_pct: result.body_fat_pct, category: result.category,
                    category_key: result.category_key, sum_mm: result.sum_mm,
                    body_density: result.body_density, fat_mass_kg: result.fat_mass_kg,
                    lean_mass_kg: result.lean_mass_kg, formula_label: result.formula_label,
                  }}
                  testid="calc-save-kaliper"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* BF % + category */}
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 text-yellow-800 text-xs font-bold uppercase tracking-widest mb-3">
                    <Activity size={14} />
                    Testzsírszázalék
                  </div>
                  <div
                    className="text-5xl font-bold text-gray-900 mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                    data-testid="caliper-bf-pct"
                  >
                    {result.body_fat_pct}%
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${categoryStyle.chip}`}
                    data-testid="caliper-category"
                  >
                    {result.category}
                  </div>

                  {/* Visual scale bar */}
                  <div className="mt-4">
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${categoryStyle.bar} transition-all duration-700`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                      <span>0%</span><span>20%</span><span>40%+</span>
                    </div>
                  </div>
                </div>

                {/* Fat mass / Lean mass */}
                <div className="bg-gray-900 text-white rounded-xl p-6">
                  <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold uppercase tracking-widest mb-4">
                    <TrendingDown size={14} />
                    Testösszetétel
                  </div>
                  {result.fat_mass_kg != null ? (
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider">Zsírtömeg</div>
                        <div className="text-3xl font-bold" data-testid="caliper-fat-mass" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {result.fat_mass_kg} kg
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider">Zsírmentes tömeg</div>
                        <div className="text-3xl font-bold text-yellow-400" data-testid="caliper-lean-mass" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {result.lean_mass_kg} kg
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Add meg a testsúlyodat is a fenti mezőben, hogy megkapd a pontos <strong>zsírtömeg</strong> és
                      <strong> zsírmentes tömeg</strong> értékeket.
                    </p>
                  )}
                </div>
              </div>

              {/* Formula / metadata */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
                <span><strong className="text-gray-700">Formula:</strong> {result.formula_label}</span>
                <span><strong className="text-gray-700">Σ redő:</strong> {result.sum_mm} mm</span>
                <span><strong className="text-gray-700">Testsűrűség:</strong> {result.body_density} g/cm³</span>
                <span><strong className="text-gray-700">Siri:</strong> BF% = 495/BD − 450</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CaliperCalculator;
